/* eslint-disable max-lines */
import { useCallback, useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router";
import { ImageSelector, type ImageSelectorRef } from "@design-lab/features/upload-artwork";
import { toFileObject, type Side } from "@design-lab/shared";
import {
  useArtwork,
  useArtworkImage,
  useEnhancedImageSource,
  ImageUploadedBanner,
  useArtworkForm
} from "@design-lab/entities/artwork";
import { useProductColors } from "@design-lab/entities/product";
import { isUserLoggedIn } from "@design-lab/shared/lib/auth";
import { Trash2 } from "lucide-react";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { ARTWORK_PROCESSING } from "@design-lab/shared/constants";
import { useShowAiStudio } from "@design-lab/shared/lib/config";
import { validateDesignLabArtworkFile } from "common/artwork-file-types";
import { useNotification } from "@design-lab/shared/ui/notification";
import { Button } from "@design-lab/shared/ui";
import { usePlacementPosition } from "@design-lab/widgets/artboard/model/usePlacementPosition";
import { useArtworkSelection } from "@design-lab/features/weave-design-lab/model/useArtworkSelection";
import { LoadingBar } from "./LoadingBar";
import { useAutoUploadDetection } from "../model/useAutoUploadDetection";
import { useStartDesigning } from "../model/useStartDesigning";
import { StartDesigningModal } from "./StartDesigningModal";
import { AiStudioButton } from "./AiStudioButton";
import { useDesignLibrarySelection } from "../model/useDesignLibrarySelection";

export type DesignLabImageSelectorProps = {
  side: Side;
};

export function DesignLabImageSelector({ side }: DesignLabImageSelectorProps) {
  const { isDesktop } = useResponsive();
  const [searchParams, setSearchParams] = useSearchParams();
  const { uploadArtwork, deleteArtwork } = useArtworkImage();
  const shouldShowAiStudio = useShowAiStudio();
  const { getMachineBySide } = useArtwork();
  const machine = getMachineBySide(side);
  const { artworkId, source } = useEnhancedImageSource({ side });
  const { error: notifyError } = useNotification();
  const isLoggedIn = isUserLoggedIn();
  const isBack = side === "back";
  const { currentProduct } = useProductColors();
  const { clearSelection } = useArtworkSelection();

  const { lens } = useArtworkForm();
  const { placement: selectedFrontPlacement } = usePlacementPosition(
    lens.focus("selectedFrontPlacement")
  );
  const placement = side === "back" ? side : selectedFrontPlacement;

  const onArtworkRemove = useCallback(() => {
    deleteArtwork(side, artworkId);
    clearSelection(side);
  }, [deleteArtwork, side, artworkId, clearSelection]);

  const { openDesignLibrary, openCanva, clearAutoUpload } = useAutoUploadDetection();

  const handleFileDrop = async (droppedFiles: File[]) => {
    const file = droppedFiles[0];
    if (!file) return;

    const { valid, message } = await validateDesignLabArtworkFile(file);
    if (!valid) {
      notifyError({ title: "Upload Failed", description: message });
      return;
    }

    try {
      await uploadArtwork({ file, side });
    } catch (error) {
      const errorData = (error as any)?.response?.data;
      const errorMessage =
        errorData?.validation_error ||
        errorData?.error ||
        "Failed to upload file. Please try again.";

      window.Honeybadger?.notify("DesignLabImageSelector handleFileDrop error", {
        params: {
          error: error instanceof Error ? error.message : String(error),
          error_object: error instanceof Error ? error.toString() : String(error),
          stack_trace: error instanceof Error ? error.stack : null,
          component: "DesignLabImageSelector",
          action: "handleFileDrop",
          side
        }
      });

      notifyError({
        title: "Upload Failed",
        description: errorMessage
      });
    }
  };

  const { resetStartDesigning, isStartDesigningOpen, onContinueDesigning } = useStartDesigning();
  const [shouldOpenLibrary, setShouldOpenLibrary] = useState(false);
  const [shouldOpenCanvaFromParent, setShouldOpenCanvaFromParent] = useState(false);

  // Open other modals only if Start Designing is not active
  useEffect(() => {
    if (openDesignLibrary && isLoggedIn && !isBack) {
      setShouldOpenLibrary(true);
    } else if (openCanva && isLoggedIn && !isBack) {
      setShouldOpenCanvaFromParent(true);
    }
  }, [openDesignLibrary, openCanva, isLoggedIn, isBack]);

  const handleQueryParams = useCallback(() => {
    const hasDesignLibraryParam = searchParams.get("design-library") === "true";
    const hasCanvaParam = searchParams.get("canva") === "true";
    const hasAutoUploadParams =
      searchParams.get("artwork_urls") && searchParams.get("file_content_type");
    const hasCatalogNumber = searchParams.get("catalog_number");
    const hasAc = searchParams.get("ac");

    if (hasAutoUploadParams || (hasCatalogNumber && hasAc)) return;

    if (hasDesignLibraryParam && isLoggedIn && !isBack) {
      setShouldOpenLibrary(true);
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("design-library");
      setSearchParams(newParams, { replace: true });
    } else if (hasCanvaParam && isLoggedIn && !isBack) {
      setShouldOpenCanvaFromParent(true);
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("canva");
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams, isLoggedIn, isBack]);

  useEffect(() => {
    handleQueryParams();
  }, [handleQueryParams]);

  const onCanvaAutoOpenCompleteFromParent = useCallback(() => {
    setShouldOpenCanvaFromParent(false);
    if (openCanva) clearAutoUpload();
  }, [openCanva, clearAutoUpload]);

  // Create ref for ImageSelector
  const imageSelectorRef = useRef<ImageSelectorRef>(null);

  const onLibraryAutoOpenComplete = useCallback(() => {
    setShouldOpenLibrary(false);
    if (openDesignLibrary) clearAutoUpload();
  }, [openDesignLibrary, clearAutoUpload]);

  // Canva file handler
  const handleCanvaFileReady = useCallback(
    async (file: File) => {
      try {
        await uploadArtwork({ file, side, source: "canva" });
      } catch (error) {
        const errorData = (error as any)?.response?.data;
        const errorMessage =
          errorData?.validation_error ||
          errorData?.error ||
          "Failed to upload file from Canva. Please try again.";

        window.Honeybadger?.notify("DesignLabImageSelector handleCanvaFileReady error", {
          params: {
            error: error instanceof Error ? error.message : String(error),
            error_object: error instanceof Error ? error.toString() : String(error),
            stack_trace: error instanceof Error ? error.stack : null,
            component: "DesignLabImageSelector",
            action: "handleCanvaFileReady",
            side
          }
        });

        notifyError({
          title: "Upload Failed",
          description: errorMessage
        });
      }
    },
    [uploadArtwork, side, notifyError]
  );

  // My Design Library handler
  const { selectArtwork } = useDesignLibrarySelection({
    placement,
    onSuccess: useCallback(() => {
      // Library will close itself after successful selection
    }, [])
  });

  const isFileUploaded = Boolean(machine.state.context.artworkId);
  const { file, originalImageUrl } = machine.state.context;
  const isStartDesigningShown = isStartDesigningOpen && !isBack;

  // Handle different rendering states
  if (!isStartDesigningShown && machine.state.matches("finished") && source.enhancedImage) {
    return <ImageUploadedBanner image={{ url: source.enhancedImage.base64 }} side={side} />;
  }

  if (!isStartDesigningShown && isFileUploaded && file) {
    return <ImageUploadedBanner image={toFileObject(file, originalImageUrl)} side={side} />;
  }

  if (!isStartDesigningShown && machine.state.hasTag("loading")) {
    return (
      <div className="flex flex-1 flex-col gap-2">
        <LoadingBar
          side={side}
          startTime={machine.state.context.startTime}
          estimatedDuration={ARTWORK_PROCESSING.ESTIMATED_DURATION}
        />
        {isFileUploaded && (
          <div className="flex justify-center">
            <Button
              data-testid="delete-image-button"
              variant="outline"
              size="sm"
              onPress={onArtworkRemove}
            >
              <Trash2 className="size-4 text-red-600" />
              Delete image
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <ImageSelector
        ref={imageSelectorRef}
        side={side}
        title={
          <>
            <span className="capitalize">{side}</span> of Shirt
          </>
        }
        onFileDrop={handleFileDrop}
        isLoggedIn={isLoggedIn}
        isDesktop={isDesktop}
        shouldOpenLibrary={shouldOpenLibrary}
        onLibraryAutoOpenComplete={onLibraryAutoOpenComplete}
        shouldOpenCanvaFromParent={shouldOpenCanvaFromParent}
        onCanvaAutoOpenCompleteFromParent={onCanvaAutoOpenCompleteFromParent}
        additionalButton={shouldShowAiStudio ? <AiStudioButton side={side} /> : undefined}
        onCanvaFileReady={handleCanvaFileReady}
        onLibraryDesignSelect={selectArtwork}
        isWaitingForCatalogNumber={!currentProduct?.catalogNumber}
      />
      {isStartDesigningShown && (
        <StartDesigningModal
          isOpen={isStartDesigningOpen}
          isLoggedIn={isLoggedIn}
          onDropFiles={(files: File[]) =>
            resetStartDesigning().then(() => {
              handleFileDrop(files);
            })
          }
          onClose={onContinueDesigning}
          onChooseDevice={() => {
            resetStartDesigning().then(() => {
              imageSelectorRef.current?.openDevice();
            });
          }}
          onChooseLibrary={() => {
            resetStartDesigning().then(() => {
              imageSelectorRef.current?.openLibrary();
            });
          }}
          onChooseCanva={() => {
            resetStartDesigning().then(() => {
              imageSelectorRef.current?.openCanva();
            });
          }}
        />
      )}
    </>
  );
}
